import * as yup from 'yup';
import { FormFieldType, SurveyForm, SurveyPart } from '../../extViewmodels';
import { computeVisibleFields } from '../containers/SurveyResponses/shared/hooks/useConditionalVisibility';
import { SURVEY_FILE_INPUT_MAX_FILES, buildFileYupSchema } from '../containers/SurveyResponses/shared/FileUpload/utils';

/**
 * Builds a yup validation schema for custom survey properties.
 *
 * Handles all field types including FileUpload (array validation)
 * and standard text-based fields (string validation).
 *
 * When `surveyForm` is provided, required-checks for fields that are hidden by
 * conditional visibility rules are skipped at validation time (evaluated against
 * sibling answer values). This includes fields nested inside V2 SurveySection
 * parts — their fieldIds are collected from the nested questions[] array so they
 * receive the 'required-if-visible' test rather than unconditional required().
 *
 * FileUpload fields follow a three-branch rule:
 *   1. `isOptional` — max-files cap enforced; no required check (regardless of visibility).
 *   2. Required and governed by surveyParts — required-if-visible (a required file inside a
 *      hidden section does NOT block submission); max-files cap always enforced.
 *   3. Required but absent from surveyParts (orphan/stale surveyForm) — unconditional
 *      required via buildFileYupSchema, same as fields with no surveyForm at all.
 *
 * Used by Event Registration, Donation, Custom, and other survey response forms.
 */
export const buildCustomPropertiesSchema = (
	fields: { id: string; isHidden?: boolean; isOptional?: boolean; label: string; fieldType?: string }[],
	surveyForm?: SurveyForm
) => {
	// Set of fieldIds represented in surveyForm.surveyParts. Used to distinguish
	// "field is governed by surveyForm and not currently visible" (skip required check)
	// from "field is not represented in surveyForm at all" (fall back to standard required).
	// The latter happens when surveyForm is stale or partial — e.g., a field was added
	// to customForm before surveyForm was repopulated.
	//
	// V2 SurveySection parts have _type === 'SurveySection' and carry no top-level
	// fieldId; their questions are nested under questions[]. We use the _type
	// discriminator directly (matching isSurveySection in shared/types.ts) to
	// avoid a cross-tree import that would create a circular dependency.
	const surveyPartFieldIds = surveyForm?.surveyParts?.length
		? new Set(
				surveyForm.surveyParts.flatMap((part: SurveyPart) => {
					// V2 section: collect fieldIds from nested questions
					if ((part as { _type?: string })._type === 'SurveySection') {
						const questions = (part as { questions?: Array<{ fieldId?: string }> }).questions ?? [];
						return questions.map(q => q.fieldId).filter((id): id is string => !!id);
					}
					// Flat part: use its own fieldId
					return [(part as { fieldId?: string }).fieldId].filter((id): id is string => !!id);
				})
			)
		: undefined;

	return yup.object().shape(
		fields.reduce(
			(schema, field) => {
				if (field.isHidden) return schema;
				if (field.fieldType === FormFieldType.FileUpload) {
					if (field.isOptional) {
						// Optional FileUpload: max-files cap only; no min required
						schema[field.id] = yup
							.array()
							.of(yup.mixed())
							.max(
								SURVEY_FILE_INPUT_MAX_FILES,
								`${field.label} can only have up to ${SURVEY_FILE_INPUT_MAX_FILES} files`
							)
							.optional();
					} else if (surveyPartFieldIds?.has(field.id)) {
						// Required FileUpload governed by surveyParts: skip required check when field is hidden.
						// Max-files cap is always enforced.
						schema[field.id] = yup
							.array()
							.of(yup.mixed())
							.max(
								SURVEY_FILE_INPUT_MAX_FILES,
								`${field.label} can only have up to ${SURVEY_FILE_INPUT_MAX_FILES} files`
							)
							.test('required-if-visible', `${field.label} is required`, function (value) {
								const visibleIds = computeVisibleFields(surveyForm, this.parent);
								if (!visibleIds.has(field.id)) return true; // Hidden → skip required check
								return Array.isArray(value) && value.length >= 1;
							});
					} else {
						// Orphan field (not in surveyParts, or no surveyForm): unconditional required
						schema[field.id] = buildFileYupSchema(field as any);
					}
					return schema;
				}
				const errorMessage = `${field.label} is required`;
				if (field.isOptional) {
					schema[field.id] = yup.string().optional();
				} else if (surveyPartFieldIds?.has(field.id)) {
					schema[field.id] = yup.string().test('required-if-visible', errorMessage, function (value) {
						const visibleIds = computeVisibleFields(surveyForm, this.parent);
						if (!visibleIds.has(field.id)) return true;
						return !!value && value.trim() !== '';
					});
				} else {
					schema[field.id] = yup.string().required(errorMessage);
				}
				return schema;
			},
			{} as Record<string, any>
		)
	);
};
